import React, { ReactNode, useCallback, useEffect } from 'react';
import { TreeViewDataItem, TreeView, DrawerPanelContent, DrawerPanelBody } from '@patternfly/react-core';
import { ExclamationCircleIcon, LockIcon } from '@patternfly/react-icons'
import { useSelector } from 'react-redux';

import './customization-page.css';
import { selectCustomizationTreeViewItems } from '../../store/selectors';
import { CustomizationTreeViewDataItem } from '../../store/model-customization-types';
import { useTranslation } from 'react-i18next';
import { convertTreeViewDataIntoItemHierarchy } from '../../store/model-customization-helpers';

interface CustomizationSideBarProps {
    treeViewData: TreeViewDataItem[],
    onTreeViewDataUpdate: (data: TreeViewDataItem[]) => void,
    onTreeViewItemSelect: (item: TreeViewDataItem) => void,
    activeTreeViewItems: TreeViewDataItem[],
}

const getTreeViewItemName = (item: CustomizationTreeViewDataItem, noLicenseWarning: string): ReactNode => {
    const label = `${item.label}${item.isModified ? ' *' : ''}`;
    const labelNode = item.isAvailable ? label : (<span data-test='customization-sidebar' data-testid={label} className="tree-view-no-license" title={noLicenseWarning}><span className="label">{label}</span><LockIcon /></span>);

    if (item.isValid) {
        return labelNode;
    } else {
        return (<span className="tree-view-validation-error"><ExclamationCircleIcon /><span className="label">{labelNode}</span></span>)
    }
}

const CustomizationSideBar = (props: CustomizationSideBarProps) => {
    const { onTreeViewDataUpdate, treeViewData, onTreeViewItemSelect, activeTreeViewItems } = props;

    const { t } = useTranslation();

    const customizationHierarchy = useSelector(selectCustomizationTreeViewItems);

    const noLicenseWarning = t('customizationPage.noLicense.tooltip');

    // generate tree view data items
    useEffect(() => {
        // pre-populate common body part groupings in correct order
        const headerItems: TreeViewDataItem[] = [
            { name: t('bodyPart.category.head'), id: 'Head', children: undefined },
            { name: t('bodyPart.category.torso'), id: 'Torso', children: undefined },
            { name: t('bodyPart.category.pelvis'), id: 'Pelvis', children: undefined },
            { name: t('bodyPart.category.combination'), id: 'Combination', children: undefined },
        ].map((item: TreeViewDataItem) => ({...item, name: <span data-test='customization-sidebar' data-testid={item.id}>{item.name}</span>}));

        
        const data = convertTreeViewDataIntoItemHierarchy(customizationHierarchy, getTreeViewItemName, noLicenseWarning, headerItems);

        onTreeViewDataUpdate(data);

    }, [customizationHierarchy, noLicenseWarning, onTreeViewDataUpdate, t]);

    const handleSelect = useCallback((_, item: TreeViewDataItem) => {
        if (item.id === undefined) { throw new Error('TreeView item has no ID'); }
        onTreeViewItemSelect(item);
    }, [onTreeViewItemSelect]);

    if (treeViewData.length < 2) {
        // patternfly's TreeView will crash if we try to render a 0-sized data array, so just return an empty component
        // if we're still initializing data
        // also don't display anything if we only have one item (the "All structures" item) since that means we haven't loaded
        // in any models/customizations yet

        return null;
    }

    return (
        <DrawerPanelContent hasNoBorder defaultSize="300px">
            <DrawerPanelBody hasNoPadding>
                <div className="mv-compact-tabs">
                    <TreeView
                        data={treeViewData}
                        onSelect={handleSelect}
                        // activeItems={[{ name: '', id: activeTreeViewId }]}
                        activeItems={activeTreeViewItems}
                        hasGuides
                        className="customization-tree-view"
                        useMemo
                    />
                </div>
            </DrawerPanelBody>
        </DrawerPanelContent>
    );
}

export default CustomizationSideBar;
